import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { User, BookOpen, CreditCard, FileText, CheckCircle, Clock, AlertCircle, Edit, X, Save, Download, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

import { getDocumentUrl } from '../utils/documentUrl';
import { generateAdmissionReceipt } from '../utils/receiptPdf';

// Static tabs are replaced by dynamic tabs inside the component

const PROFILE_DOCUMENT_LABELS = {
  photo: 'Passport Photo',
  aadhar: 'Aadhar Card',
  // previousSchoolTC: 'Transfer Certificate (TC)',
  marksheet10: '10th Mark Sheet',
  marksheet12: '12th Mark Sheet / Diploma Certificate',
  diplomaCertificate: 'Diploma Certificate',
  tc: 'Transfer Certificate',
  community: 'Community Certificate',
  birthCertificate: 'Birth Certificate',
  admissionForm: 'Admission Form',
  disabilityCertificate: 'Disability Certificate',
};

const PROFILE_DOCUMENT_ORDER = [
  'photo',
  'aadhar',
  'birthCertificate',
  'community',
  'marksheet10',
  'marksheet12',
  // 'previousSchoolTC',
  // 'diplomaCertificate',
  'tc',
  'admissionForm',
  'disabilityCertificate',
];

const MOBILE_REGEX = /^[0-9]{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PHONE_TOAST = {
  mobile: {
    id: 'profile-student-mobile-length',
    message: 'Student contact number must be exactly 10 digits',
  },
  parentMobile: {
    id: 'profile-parent-mobile-length',
    message: 'Parent contact number must be exactly 10 digits',
  },
};

const isPwdEnabled = (value) => value === true || value === 'true' || value === 'Yes';
const isAcceptedDocument = (value = {}) => String(value?.status || '').toLowerCase() === 'accepted';

const normalizeProfileDocuments = (documents = {}) => Object.entries(documents || {}).reduce((acc, [rawKey, rawValue]) => {
  const key = String(rawKey).replace(/^doc_/, '');
  if (!key) return acc;
  acc[key] = typeof rawValue === 'string'
    ? { url: rawValue, originalName: rawValue.split('/').pop() || key }
    : { ...rawValue, url: rawValue?.url || rawValue?.path || '' };
  return acc;
}, {});

const getDocumentRequirements = (courseName, category, profile) => {
  if (!courseName) return [];
  const docs = [
    { id: 'aadhar', label: 'Aadhar Card *' },
    { id: 'birthCertificate', label: 'Birth Certificate' },
    { id: 'community', label: 'Community Certificate' },
  ];
  
  const name = courseName.toLowerCase();
  
  if (category === 'K-12') {
    if (name.includes('grade 11') || name.includes('grade 12')) {
      docs.push({ id: 'marksheet10', label: '10th Mark Sheet' });
      docs.push({ id: 'previousSchoolTC', label: 'Transfer Certificate (TC)' });
    } else if (!name.includes('pre kg')) {
      docs.push({ id: 'previousSchoolTC', label: 'Transfer Certificate (TC)' });
    }
  } else {
    docs.push({ id: 'marksheet10', label: '10th Mark Sheet' });
    docs.push({ id: 'marksheet12', label: '12th Mark Sheet / Diploma Certificate' });
  }
  docs.push({ id: 'admissionForm', label: 'Admission Form' });

  if (isPwdEnabled(profile?.isPwd)) {
    docs.push({ id: 'disabilityCertificate', label: 'Disability Certificate *' });
  }

  return docs;
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('Personal Information');
  const [profile, setProfile] = useState(null);
  const [admissions, setAdmissions] = useState([]);
  const [profileDocuments, setProfileDocuments] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null); // adm object
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [otpDialog, setOtpDialog] = useState({
    open: false,
    emailNeeded: false,
    mobileNeeded: false,
    emailOtp: '',
    mobileOtp: '',
    sending: false,
    verifying: false,
    devHints: {},
  });
  const [editForm, setEditForm] = useState({
    fullName: '',
    mobile: '',
    dob: '',
    community: '',
    parentName: '',
    parentOccupation: '',
    parentMobile: '',
    homeTown: '',
    district: '',
    districtOther: '',
    currentQualification: '',
    aadhar: '',
    email: '',
    gender: '',
    genderOther: '',
    isPwd: '',
    disabilityType: '',
    disabilityTypeOther: '',
    disabilityPercentage: '',
    udidNumber: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [profileRes, documentsRes] = await Promise.all([
        axios.get('/api/students/profile'),
        axios.get('/api/users/profile/documents')
      ]);
      setProfile(profileRes.data.profile);
      setProfileDocuments(normalizeProfileDocuments(documentsRes.data?.profileDocuments || documentsRes.data || {}));
    } catch (err) {
      console.error(err);
      toast.error('Could not load profile history');
    }
  };

  const validatePhoneField = (field, { required = false, showToast = false } = {}) => {
    const value = editForm[field] || '';
    const config = PHONE_TOAST[field];

    if (!value) return !required;

    const isValid = MOBILE_REGEX.test(value);
    if (!isValid && showToast && config) {
      toast.error(config.message, { id: config.id });
    }

    return isValid;
  };

  const handlePhoneBlur = (field, required = false) => {
    validatePhoneField(field, { required, showToast: true });
  };

  const buildProfilePayload = (extra = {}) => {
    const profileFields = {
      community: editForm.community,
      parentName: editForm.parentName,
      parentOccupation: editForm.parentOccupation,
      parentMobile: editForm.parentMobile,
      homeTown: editForm.homeTown,
      district: editForm.district,
      districtOther: editForm.districtOther,
      currentQualification: editForm.currentQualification,
      aadhar: editForm.aadhar,
      gender: editForm.gender,
      genderOther: editForm.gender === 'Others' ? editForm.genderOther : '',
      isPwd: editForm.isPwd === 'Yes' ? true : (editForm.isPwd === 'No' ? false : undefined),
      disabilityType: editForm.disabilityType,
      disabilityTypeOther: editForm.disabilityType === 'Other' ? editForm.disabilityTypeOther : '',
      disabilityPercentage: editForm.disabilityPercentage,
      udidNumber: editForm.udidNumber
    };

    return {
      name: editForm.fullName,
      email: editForm.email,
      mobile: editForm.mobile,
      dob: editForm.dob,
      fullName: editForm.fullName,
      ...profileFields,
      profile: profileFields,
      ...extra,
    };
  };

  const saveProfileChanges = async (extra = {}) => {
    const res = await axios.put('/api/students/profile', buildProfilePayload(extra));
    if (res.data?.user) updateUser?.(res.data.user);
    await fetchProfile();
  };

  const identityChanges = () => ({
    emailChanged: Boolean(profile?.email && editForm.email.trim().toLowerCase() !== String(profile.email || '').toLowerCase()),
    mobileChanged: Boolean(profile?.mobile && editForm.mobile.replace(/\D/g, '').slice(0, 10) !== String(profile.mobile || '')),
  });

  const sendProfileOtp = async ({ emailChanged, mobileChanged }) => {
    setOtpDialog(prev => ({ ...prev, sending: true, devHints: {} }));
    try {
      const hints = {};
      if (emailChanged) {
        const res = await axios.post('/api/auth/send-otp', { contact: editForm.email.trim().toLowerCase(), type: 'email' });
        if (res.data?.devOtp) hints.email = res.data.devOtp;
      }
      if (mobileChanged) {
        const res = await axios.post('/api/auth/send-otp', { contact: editForm.mobile.replace(/\D/g, '').slice(0, 10), type: 'mobile' });
        if (res.data?.devOtp) hints.mobile = res.data.devOtp;
      }
      setOtpDialog(prev => ({ ...prev, devHints: hints }));
      toast.success('OTP sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setOtpDialog(prev => ({ ...prev, sending: false }));
    }
  };

  const handleDoneEditing = async () => {
    if (!editForm.fullName || !editForm.email || !editForm.mobile || !editForm.dob) {
      toast.error('Please complete all required profile fields', { id: 'profile-required-fields' });
      setSaveStatus('error');
      return;
    }
    if (!EMAIL_REGEX.test(editForm.email.trim().toLowerCase())) {
      toast.error('Please enter a valid email address');
      setSaveStatus('error');
      return;
    }

    if (!validatePhoneField('mobile', { required: true, showToast: true })) {
      setSaveStatus('error');
      return;
    }

    if (!validatePhoneField('parentMobile', { showToast: true })) {
      setSaveStatus('error');
      return;
    }

    const changes = identityChanges();
    if (changes.emailChanged || changes.mobileChanged) {
      setOtpDialog({
        open: true,
        emailNeeded: changes.emailChanged,
        mobileNeeded: changes.mobileChanged,
        emailOtp: '',
        mobileOtp: '',
        sending: false,
        verifying: false,
        devHints: {},
      });
      await sendProfileOtp(changes);
      return;
    }

    setSaveStatus('saving');
    try {
      await saveProfileChanges();
      setSaveStatus('saved');
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setSaveStatus('error');
      toast.error(err.response?.data?.message || 'Failed to save profile details', { id: 'profile-save-error' });
    }
  };

  useEffect(() => {
    if (!isEditing || !profile) return;

    // Check if form values actually changed from current profile values
    const hasChanged =
      editForm.fullName !== (profile.fullName || '') ||
      editForm.mobile !== (profile.mobile || '') ||
      editForm.dob !== (profile.dob || '') ||
      editForm.community !== (profile.community || '') ||
      editForm.parentName !== (profile.parentName || '') ||
      editForm.parentOccupation !== (profile.parentOccupation || '') ||
      editForm.parentMobile !== (profile.parentMobile || '') ||
      editForm.homeTown !== (profile.homeTown || '') ||
      editForm.district !== (profile.district || '') ||
      editForm.districtOther !== (profile.districtOther || '') ||
      editForm.currentQualification !== (profile.currentQualification || '') ||
      editForm.aadhar !== (profile.aadhar || '') ||
      editForm.email !== (profile.email || '') ||
      editForm.gender !== (profile.gender || '') ||
      editForm.genderOther !== (profile.genderOther || '') ||
      editForm.isPwd !== (isPwdEnabled(profile.isPwd) ? 'Yes' : profile.isPwd === false || profile.isPwd === 'false' || profile.isPwd === 'No' ? 'No' : '') ||
      editForm.disabilityType !== (profile.disabilityType || '') ||
      editForm.disabilityTypeOther !== (profile.disabilityTypeOther || '') ||
      editForm.disabilityPercentage !== (profile.disabilityPercentage || '') ||
      editForm.udidNumber !== (profile.udidNumber || '');

    if (!hasChanged) {
      setSaveStatus('saved');
      return;
    }

    if (!editForm.fullName || !editForm.email || !editForm.mobile || !editForm.dob) {
      setSaveStatus('error');
      return;
    }

    if (!MOBILE_REGEX.test(editForm.mobile)) {
      setSaveStatus('error');
      return;
    }
    if (!EMAIL_REGEX.test(editForm.email.trim().toLowerCase())) {
      setSaveStatus('error');
      return;
    }
    const changes = identityChanges();
    if (changes.emailChanged || changes.mobileChanged) {
      setSaveStatus('idle');
      return;
    }
    if (editForm.parentMobile && !MOBILE_REGEX.test(editForm.parentMobile)) {
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');

    const delayDebounce = setTimeout(async () => {
      try {
        await saveProfileChanges();
        setSaveStatus('saved');
      } catch (err) {
        console.error(err);
        setSaveStatus('error');
      }
    }, 1000); // 1-second debounce

    return () => clearTimeout(delayDebounce);
  }, [editForm, isEditing]);

  const profileRequiresDisabilityCertificate = isPwdEnabled(profile?.isPwd);
  const visibleProfileDocumentOrder = PROFILE_DOCUMENT_ORDER.filter(
    (docKey) => docKey !== 'disabilityCertificate' || profileRequiresDisabilityCertificate
  );
  const profileDocumentItems = visibleProfileDocumentOrder
    .map((docKey) => ({
      id: docKey,
      label: PROFILE_DOCUMENT_LABELS[docKey] || docKey,
      value: profileDocuments?.[docKey] || null,
      isUploaded: Boolean(profileDocuments?.[docKey]?.url),
    }));
  const pendingProfileDocumentItems = profileDocumentItems.filter((doc) => !doc.isUploaded);
  const uploadedProfileDocumentItems = profileDocumentItems.filter((doc) => doc.isUploaded);

  const tabsList = ['Personal Information'];
  if (profile) {
    tabsList.push('Documents');
  }

  useEffect(() => {
    const section = String(searchParams.get('section') || '').toLowerCase();
    if (section === 'documents' && profile) {
      setActiveTab('Documents');
    }
  }, [searchParams, profile]);

  useEffect(() => {
    if (activeTab === 'Documents' && !profile) {
      setActiveTab('Personal Information');
    }
  }, [activeTab, profile]);
  const handleCancelAdmission = async () => {
    if (!confirmCancel) return;
    const adm = confirmCancel;

    setLoading(true);
    setConfirmCancel(null);
    try {
      await axios.post(`/api/applications/${adm.applicationId}/cancel`);
      toast.success("Admission cancelled successfully. You can now book another course.");
      fetchProfile();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Failed to cancel admission");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPendingDoc = async (docId, file) => {
    if (!file) return;

    if (docId === 'admissionForm' && !applicationNo.trim()) {
      toast.error('Please enter the Application Number before uploading the form.');
      return;
    }

    setUploadProgress(prev => ({ ...prev, [docId]: 0 }));
    try {
      const fd = new FormData();
      fd.append(`doc_${docId}`, file);
      await axios.post('/api/users/profile/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [docId]: percent }));
        }
      });
      const documentsRes = await axios.get('/api/users/profile/documents');
      setProfileDocuments(normalizeProfileDocuments(documentsRes.data?.profileDocuments || documentsRes.data || {}));
      toast.success('Document uploaded successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload document');
    } finally {
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    }
  };

  const handleRemoveDoc = async (docKey) => {
    setLoading(true);
    try {
      const res = await axios.delete(`/api/users/profile/documents/${docKey}`);
      setProfileDocuments(normalizeProfileDocuments(res.data?.profileDocuments || res.data || {}));
      toast.success("Document removed successfully.");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Failed to remove document");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyProfileOtpAndSave = async () => {
    if (otpDialog.emailNeeded && otpDialog.emailOtp.trim().length < 4) return toast.error('Please enter email OTP');
    if (otpDialog.mobileNeeded && otpDialog.mobileOtp.trim().length < 4) return toast.error('Please enter mobile OTP');
    setOtpDialog(prev => ({ ...prev, verifying: true }));
    setSaveStatus('saving');
    try {
      await saveProfileChanges({
        ...(otpDialog.emailNeeded ? { emailOtp: otpDialog.emailOtp.trim() } : {}),
        ...(otpDialog.mobileNeeded ? { mobileOtp: otpDialog.mobileOtp.trim() } : {}),
      });
      setOtpDialog(prev => ({ ...prev, open: false, verifying: false }));
      setSaveStatus('saved');
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error(err);
      setSaveStatus('error');
      toast.error(err.response?.data?.message || 'Failed to verify OTP');
      setOtpDialog(prev => ({ ...prev, verifying: false }));
    }
  };

  const resendProfileOtp = () => {
    sendProfileOtp({ emailChanged: otpDialog.emailNeeded, mobileChanged: otpDialog.mobileNeeded });
  };

  const handleDownloadReceipt = async (adm) => {
    const toastId = toast.loading("Generating receipt...");
    try {
      const studentName = profile?.fullName || user?.name || 'Student';
      await generateAdmissionReceipt({
        application: adm,
        applicationId: adm.applicationId,
        paymentId: adm.paymentId,
        studentName
      });
      toast.success("Receipt downloaded!", { id: toastId });
    } catch (err) {
      console.error("PDF Error:", err);
      toast.error("Receipt generation failed.", { id: toastId });
    }
  };

  const StatusBadge = ({ status }) => {
    const cfg = {
      confirmed: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: CheckCircle, label: 'Pre Registration Confirmed' },
      pending: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: Clock, label: 'Pending' },
      rejected: { color: '#EF4444', bg: 'rgba(239,68,68,0.12)', icon: AlertCircle, label: 'Rejected' },
      cancelled: { color: '#6B7280', bg: 'rgba(107,114,128,0.12)', icon: X, label: 'Cancelled' },
    }[status?.toLowerCase()] || { color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)', icon: Clock, label: status };
    const Icon = cfg.icon;
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: cfg.bg, color: cfg.color }}>
        <Icon size={11} /> {cfg.label}
      </span>
    );
  };

  const Field = ({ label, value }) => (
    <div>
      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-sm font-medium">{value || '—'}</p>
    </div>
  );

  return (
    <div className="min-h-screen pt-8 pb-32 md:pb-8 px-4" style={{ background: 'var(--bg)' }}>
      <div className="max-w-3xl mx-auto">

        {/* Profile Header */}
        <div className="rounded-2xl p-6 mb-6" style={{ background: 'var(--card)', border: '1px solid var(--card-border)' }}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: 'var(--primary)' }}>
              {(profile?.fullName || user?.name || 'S')[0].toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Poppins' }}>
                {profile?.fullName || user?.name || 'Student'}
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{profile?.email || user?.email}</p>
              {admissions.length > 0 && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs"
                  style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
                  <CheckCircle size={10} /> Enrolled
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto tabs-scroll">
          {tabsList.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: activeTab === tab ? 'var(--primary)' : 'var(--card)',
                color: activeTab === tab ? '#fff' : 'var(--text-muted)',
                border: `1px solid ${activeTab === tab ? 'var(--primary)' : 'var(--card-border)'}`,
              }}>
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--card-border)' }}>
          {activeTab === 'Personal Information' && (
            <>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
                  <User size={18} style={{ color: 'var(--primary)' }} /> Personal Information
                </h2>
                {profile && !isEditing && (
                  <button
                    onClick={() => {
                      setEditForm({
                        fullName: profile.fullName || '',
                        mobile: profile.mobile || '',
                        dob: profile.dob || '',
                        community: profile.community || '',
                        parentName: profile.parentName || '',
                        parentOccupation: profile.parentOccupation || '',
                        parentMobile: profile.parentMobile || '',
                        homeTown: profile.homeTown || '',
                        district: profile.district || '',
                        districtOther: profile.districtOther || '',
                        currentQualification: profile.currentQualification || '',
                        aadhar: profile.aadhar || '',
                        email: profile.email || '',
                        gender: profile.gender || '',
                        genderOther: profile.genderOther || '',
                        isPwd: isPwdEnabled(profile.isPwd) ? 'Yes' : (profile.isPwd === false || profile.isPwd === 'false' || profile.isPwd === 'No' ? 'No' : ''),
                        disabilityType: profile.disabilityType || '',
                        disabilityTypeOther: profile.disabilityTypeOther || '',
                        disabilityPercentage: profile.disabilityPercentage || '',
                        udidNumber: profile.udidNumber || ''
                      });
                      setIsEditing(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-extrabold bg-slate-900 text-white hover:bg-black transition-all cursor-pointer shadow-sm hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <Edit size={12} /> Edit Profile
                  </button>
                )}
                {profile && isEditing && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1 rounded-full transition-all border"
                      style={{
                        borderColor: saveStatus === 'saving' ? '#FFE4E6' : saveStatus === 'saved' ? '#D1FAE5' : saveStatus === 'error' ? '#FEE2E2' : 'transparent',
                        background: saveStatus === 'saving' ? '#FFF1F2' : saveStatus === 'saved' ? '#ECFDF5' : saveStatus === 'error' ? '#FEF2F2' : 'transparent',
                        color: saveStatus === 'saving' ? '#E11D48' : saveStatus === 'saved' ? '#059669' : saveStatus === 'error' ? '#DC2626' : '#64748B'
                      }}>
                      {saveStatus === 'saving' && (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                          Saving...
                        </>
                      )}
                      {saveStatus === 'saved' && (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Saved successfully
                        </>
                      )}
                      {saveStatus === 'error' && (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                          Check required fields
                        </>
                      )}
                      {saveStatus === 'idle' && (
                        <>
                          All changes saved
                        </>
                      )}
                    </span>

                    <button
                      onClick={handleDoneEditing}
                      disabled={saveStatus === 'saving'}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-extrabold bg-slate-900 text-white hover:bg-black transition-all cursor-pointer shadow-sm hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      <CheckCircle size={12} /> {saveStatus === 'saving' ? 'Saving...' : 'Done'}
                    </button>
                  </div>
                )}
              </div>
              {profile ? (
                !isEditing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start grid-flow-row-dense">
                    <Field label="Student Name" value={profile.fullName} />
                    <Field label="Student Contact Number" value={profile.mobile} />
                    <Field label="Date of Birth" value={profile.dob} />
                    <Field label="Gender" value={profile.gender === 'Others' ? (profile.genderOther || 'Others') : profile.gender} />
                    <Field label="Community" value={profile.community} />
                    <Field label="Parent Name" value={profile.parentName} />
                    <Field label="Parent Occupation" value={profile.parentOccupation} />
                    <Field label="Parent Contact Number" value={profile.parentMobile} />
                    <Field label="Home Town" value={profile.homeTown} />
                    <Field label="District" value={profile.district === 'Other' ? profile.districtOther : profile.district} />
                    {!(profile.category && (profile.category.toLowerCase() === 'k12' || profile.category.toLowerCase() === 'k-12')) && (
                    <Field label="Current Qualification" value={profile.currentQualification} />
                    )}
                    <Field label="Person with Disability (PwD)?" value={isPwdEnabled(profile.isPwd) ? 'Yes' : 'No'} />
                    {isPwdEnabled(profile.isPwd) && (
                      <>
                        <Field label="Disability Type" value={profile.disabilityType === 'Other' ? (profile.disabilityTypeOther || 'Other') : profile.disabilityType} />
                        <Field label="Disability Percentage" value={profile.disabilityPercentage ? `${profile.disabilityPercentage}%` : ''} />
                        <Field label="UDID Number" value={profile.udidNumber} />
                      </>
                    )}
                    <Field label="Aadhar Number" value={profile.aadhar ? 'XXXXXXXX' + String(profile.aadhar).slice(-4) : '—'} />
                    <Field label="Email ID" value={profile.email} />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Student Name *</label>
                      <input
                        type="text"
                        value={editForm.fullName}
                        onChange={(e) => setEditForm(prev => ({ ...prev, fullName: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Student contact number *</label>
                      <input
                        type="text"
                        value={editForm.mobile}
                        onChange={(e) => setEditForm(prev => ({ ...prev, mobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) }))}
                        onBlur={() => handlePhoneBlur('mobile', true)}
                        maxLength={10}
                        placeholder="10-digit mobile number"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Date of Birth *</label>
                      <input
                        type="date"
                        value={editForm.dob}
                        onChange={(e) => setEditForm(prev => ({ ...prev, dob: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>
                    {/* Gender with inline Others text */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Gender</label>
                      <div className="flex gap-2">
                        <select
                          value={editForm.gender}
                          onChange={(e) => setEditForm(prev => ({ ...prev, gender: e.target.value, genderOther: e.target.value !== 'Others' ? '' : prev.genderOther }))}
                          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Transgender">Transgender</option>
                          <option value="Others">Others</option>
                        </select>
                        {editForm.gender === 'Others' && (
                          <input
                            type="text"
                            value={editForm.genderOther}
                            onChange={(e) => setEditForm(prev => ({ ...prev, genderOther: e.target.value }))}
                            placeholder="Please specify"
                            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                          />
                        )}
                      </div>
                    </div>

                    {/* Community */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Community</label>
                      <select
                        value={editForm.community}
                        onChange={(e) => setEditForm(prev => ({ ...prev, community: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      >
                        <option value="">Select Community</option>
                        <option value="OC">OC</option>
                        <option value="BC">BC</option>
                        <option value="BCM">BCM</option>
                        <option value="MBC">MBC</option>
                        <option value="SC">SC</option>
                        <option value="SCA">SCA</option>
                        <option value="ST">ST</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>

                    {/* Parent Name */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Parent Name</label>
                      <input
                        type="text"
                        value={editForm.parentName}
                        onChange={(e) => setEditForm(prev => ({ ...prev, parentName: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>

                    {/* Parent Occupation */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Parent Occupation</label>
                      <input
                        type="text"
                        value={editForm.parentOccupation}
                        onChange={(e) => setEditForm(prev => ({ ...prev, parentOccupation: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>

                    {/* Parent Contact */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Parent Contact Number</label>
                      <input
                        type="text"
                        value={editForm.parentMobile}
                        onChange={(e) => setEditForm(prev => ({ ...prev, parentMobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) }))}
                        onBlur={() => handlePhoneBlur('parentMobile')}
                        maxLength={10}
                        placeholder="10-digit mobile number"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>

                    {/* Home Town */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Home Town</label>
                      <input
                        type="text"
                        value={editForm.homeTown}
                        onChange={(e) => setEditForm(prev => ({ ...prev, homeTown: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>

                    {/* District */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">District</label>
                      <select
                        value={editForm.district}
                        onChange={(e) => setEditForm(prev => ({ ...prev, district: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      >
                        <option value="">Select District</option>
                        {['Ariyalur', 'Chengalpattu', 'Chennai', 'Coimbatore', 'Cuddalore', 'Dharmapuri', 'Dindigul', 'Erode', 'Kallakurichi', 'Kanchipuram', 'Kanniyakumari', 'Karur', 'Krishnagiri', 'Madurai', 'Mayiladuthurai', 'Nagapattinam', 'Namakkal', 'Nilgiris', 'Perambalur', 'Pudukkottai', 'Ramanathapuram', 'Ranipet', 'Salem', 'Sivaganga', 'Tenkasi', 'Thanjavur', 'Theni', 'Thoothukudi', 'Tiruchirappalli', 'Tirunelveli', 'Tirupathur', 'Tiruppur', 'Tiruvallur', 'Tiruvannamalai', 'Tiruvarur', 'Vellore', 'Viluppuram', 'Virudhunagar', 'Other'].map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>

                    {/* District Other */}
                    {editForm.district === 'Other' && (
                      <div>
                        <label className="block text-xs font-semibold mb-1 text-slate-500">Specify District</label>
                        <input
                          type="text"
                          value={editForm.districtOther}
                          onChange={(e) => setEditForm(prev => ({ ...prev, districtOther: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                        />
                      </div>
                    )}

                    {/* Current Qualification */}
                    {!(profile.category && (profile.category.toLowerCase() === 'k12' || profile.category.toLowerCase() === 'k-12')) && (
                      <div>
                        <label className="block text-xs font-semibold mb-1 text-slate-500">Current Qualification</label>
                        <select
                          value={editForm.currentQualification}
                          onChange={(e) => setEditForm(prev => ({ ...prev, currentQualification: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                        >
                          <option value="">Select Qualification</option>
                          <option value="10th Std (SSLC)">10th Std (SSLC)</option>
                          <option value="12th Std (HSC)">12th Std (HSC)</option>
                          <option value="Diploma Holder">Diploma Holder</option>
                          <option value="UG (Degree Completed / Pursuing)">UG (Degree Completed / Pursuing)</option>
                          <option value="PG (Degree Completed / Pursuing)">PG (Degree Completed / Pursuing)</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    )}

                    {/* PwD — Dropdown */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Person with Disability (PwD)?</label>
                      <select
                        value={editForm.isPwd}
                        onChange={(e) => setEditForm(prev => ({ ...prev, isPwd: e.target.value, disabilityType: e.target.value !== 'Yes' ? '' : prev.disabilityType, disabilityTypeOther: e.target.value !== 'Yes' ? '' : prev.disabilityTypeOther, disabilityPercentage: e.target.value !== 'Yes' ? '' : prev.disabilityPercentage, udidNumber: e.target.value !== 'Yes' ? '' : prev.udidNumber }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      >
                        <option value="">Select...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    {/* Disability sub-fields */}
                    {editForm.isPwd === 'Yes' && (
                      <>
                        {/* Disability Type + Other specify */}
                        <div>
                          <label className="block text-xs font-semibold mb-1 text-slate-500">Disability Type *</label>
                          <select
                            value={editForm.disabilityType}
                            onChange={(e) => setEditForm(prev => ({ ...prev, disabilityType: e.target.value, disabilityTypeOther: e.target.value !== 'Other' ? '' : prev.disabilityTypeOther }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                          >
                            <option value="">Select Type</option>
                            <option value="Locomotor Disability">Locomotor Disability</option>
                            <option value="Visual Impairment">Visual Impairment</option>
                            <option value="Hearing Impairment">Hearing Impairment</option>
                            <option value="Intellectual Disability">Intellectual Disability</option>
                            <option value="Multiple Disabilities">Multiple Disabilities</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        {editForm.disabilityType === 'Other' ? (
                          <div>
                            <label className="block text-xs font-semibold mb-1 text-slate-500">Please Specify Disability Type *</label>
                            <input
                              type="text"
                              value={editForm.disabilityTypeOther}
                              onChange={(e) => setEditForm(prev => ({ ...prev, disabilityTypeOther: e.target.value }))}
                              placeholder="Describe your disability type"
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs font-semibold mb-1 text-slate-500">Disability Percentage (%)</label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={editForm.disabilityPercentage}
                              onChange={(e) => setEditForm(prev => ({ ...prev, disabilityPercentage: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                            />
                          </div>
                        )}
                        {editForm.disabilityType === 'Other' && (
                          <div>
                            <label className="block text-xs font-semibold mb-1 text-slate-500">Disability Percentage (%)</label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={editForm.disabilityPercentage}
                              onChange={(e) => setEditForm(prev => ({ ...prev, disabilityPercentage: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                            />
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-semibold mb-1 text-slate-500">UDID Number (Optional)</label>
                          <input
                            type="text"
                            value={editForm.udidNumber}
                            onChange={(e) => setEditForm(prev => ({ ...prev, udidNumber: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                          />
                        </div>
                      </>
                    )}

                    {/* Aadhar */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Aadhar Number</label>
                      <input
                        type="text"
                        value={editForm.aadhar}
                        onChange={(e) => setEditForm(prev => ({ ...prev, aadhar: e.target.value }))}
                        maxLength={12}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-slate-500">Email ID *</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white"
                      />
                    </div>
                  </div>
                )
              ) : (
                <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                  <User size={40} className="mx-auto mb-3 opacity-30" />
                  <p>No profile data found. Complete your application to see details here.</p>
                </div>
              )}
            </>
          )}



          {activeTab === 'Admissions' && (
            <>
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
                <CreditCard size={18} style={{ color: 'var(--primary)' }} /> Admission History
              </h2>
              {admissions.length > 0 ? (
                <div className="space-y-4">
                  {admissions.map(adm => (
                    <div key={adm._id} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--card-border)' }}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold">{adm.courseName}</p>
                          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{adm.programName}</p>
                        </div>
                        <StatusBadge status={adm.status} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <Field label="Institution Name" value={adm.collegeName || 'SNS Institutions'} />
                        <Field label="Application ID" value={adm.applicationId} />
                        <Field label="Amount Paid" value={`₹${Number(adm.totalPaid || 0).toLocaleString('en-IN')}`} />
                        <Field label="Payment ID" value={adm.paymentId} />
                        <Field label="Date" value={adm.createdAt ? new Date(adm.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''} />
                      </div>
                      {(adm.paymentStatus === 'completed' || adm.paymentId) && adm.status !== 'cancelled' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadReceipt(adm)}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold bg-gray-900 text-white hover:bg-black transition-all"
                          >
                            <Download size={14} /> Receipt
                          </button>
                          {adm.status !== 'rejected' && adm.status !== 'cancelled' && (
                            <button
                              onClick={() => setConfirmCancel(adm)}
                              className="px-4 py-2.5 rounded-xl text-xs font-bold border border-red-100 text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                  <CreditCard size={40} className="mx-auto mb-3 opacity-30" />
                  <p>No admission records found.</p>
                  <p className="text-sm mt-1">Apply for a course to see your records here.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'Documents' && (
            <>
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
                <FileText size={18} style={{ color: 'var(--primary)' }} /> Documents
              </h2>

              <div className="rounded-xl p-6 space-y-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--card-border)' }}>
                <div>
                  <p className="font-semibold text-lg">Profile Document Center</p>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Upload and manage your reusable documents here. These files will be used to prefill upcoming applications automatically.
                  </p>
                </div>
                
                <div className="space-y-6">
                  <div className="space-y-3">
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                      <Upload size={14} /> Documents to upload ({pendingProfileDocumentItems.length})
                    </h4>

                    {pendingProfileDocumentItems.length === 0 ? (
                      <div className="rounded-xl border border-slate-900 bg-white p-4 text-sm font-semibold text-slate-600">
                        All visible profile documents have been uploaded.
                      </div>
                    ) : pendingProfileDocumentItems.map((doc) => {
                      const isUploadingThis = uploadProgress && uploadProgress[doc.id] !== undefined;
                      const currentPercent = isUploadingThis ? uploadProgress[doc.id] : 0;

                      return (
                        <div
                          key={doc.id}
                          className="flex flex-col gap-2.5 p-3 rounded-xl border border-slate-900 bg-white transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Clock size={15} className="text-slate-700" />
                                <span className="font-medium text-sm text-slate-950">
                                  {doc.label}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              {!isUploadingThis ? (
                                <label className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-colors text-center cursor-pointer flex items-center gap-2 justify-center bg-slate-900 text-white hover:bg-slate-800">
                                  <Upload size={14} /> Upload
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleUploadPendingDoc(doc.id, e.target.files?.[0])}
                                  />
                                </label>
                              ) : (
                                <span className="text-xs font-extrabold text-emerald-600 animate-pulse">
                                  Uploading... {currentPercent}%
                                </span>
                              )}
                            </div>
                          </div>

                          {isUploadingThis && (
                            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                              <div
                                className="h-full bg-emerald-500 transition-all duration-300 rounded-full"
                                style={{ width: `${currentPercent}%` }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                      <FileText size={14} /> Uploaded files ({uploadedProfileDocumentItems.length})
                    </h4>

                    {uploadedProfileDocumentItems.length === 0 ? (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-500">
                        No files uploaded yet.
                      </div>
                    ) : uploadedProfileDocumentItems.map((doc) => {
                      const isUploadingThis = uploadProgress && uploadProgress[doc.id] !== undefined;
                      const currentPercent = isUploadingThis ? uploadProgress[doc.id] : 0;
                      const value = doc.value || {};
                      const finalUrl = getDocumentUrl(value.url);
                      const isAccepted = isAcceptedDocument(value);
                      const isRejected = String(value.status || '').toLowerCase() === 'rejected';

                      return (
                        <div
                          key={doc.id}
                          className="flex flex-col gap-2.5 p-3 rounded-xl border border-slate-200 bg-white transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <CheckCircle size={15} className="text-emerald-600" />
                                <span className="font-medium text-sm text-slate-950">{doc.label}</span>
                                {/* <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-emerald-700">
                                  Uploaded
                                </span> */}
                                {isAccepted && (
                                  <span className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-emerald-700">
                                    Accepted
                                  </span>
                                )}
                                {isRejected && (
                                  <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-rose-700">
                                    Rejected
                                  </span>
                                )}
                              </div>

                              {value.uploadedAt && (
                                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                  Updated {new Date(value.uploadedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                </p>
                              )}
                              {isRejected && value.rejectionReason && (
                                <p className="text-xs font-semibold text-rose-600 mt-1">Reason: {value.rejectionReason}</p>
                              )}
                            </div>

                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              <a
                                href={finalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 sm:flex-none px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-colors text-center"
                              >
                                View
                              </a>
                              {!isUploadingThis ? (
                                !value.locked && !isAccepted && (
                                  <label className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-colors text-center cursor-pointer flex items-center gap-2 justify-center bg-emerald-600 text-white hover:bg-emerald-700">
                                    <Upload size={14} /> Replace
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => handleUploadPendingDoc(doc.id, e.target.files?.[0])}
                                    />
                                  </label>
                                )
                              ) : (
                                <span className="text-xs font-extrabold text-emerald-600 animate-pulse">
                                  Uploading... {currentPercent}%
                                </span>
                              )}
                            </div>
                          </div>

                          {isUploadingThis && (
                            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                              <div
                                className="h-full bg-emerald-500 transition-all duration-300 rounded-full"
                                style={{ width: `${currentPercent}%` }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {otpDialog.open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-100 p-6 shadow-xl animate-fade-up">
            <div className="mb-5">
              <h3 className="font-bold text-lg text-slate-900">Verify contact change</h3>
              <p className="text-sm text-slate-500 mt-1">Enter the OTP sent to the new contact details before saving.</p>
            </div>
            <div className="space-y-4">
              {otpDialog.emailNeeded && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Email OTP</label>
                  <input
                    value={otpDialog.emailOtp}
                    onChange={(e) => setOtpDialog(prev => ({ ...prev, emailOtp: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={`Sent to ${editForm.email}`}
                    inputMode="numeric"
                  />
                  {otpDialog.devHints.email && <p className="mt-1 text-xs font-bold text-indigo-600">Development OTP: {otpDialog.devHints.email}</p>}
                </div>
              )}
              {otpDialog.mobileNeeded && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Mobile OTP</label>
                  <input
                    value={otpDialog.mobileOtp}
                    onChange={(e) => setOtpDialog(prev => ({ ...prev, mobileOtp: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={`Sent to ${editForm.mobile}`}
                    inputMode="numeric"
                  />
                  {otpDialog.devHints.mobile && <p className="mt-1 text-xs font-bold text-indigo-600">Development OTP: {otpDialog.devHints.mobile}</p>}
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={() => setOtpDialog(prev => ({ ...prev, open: false }))}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-extrabold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={resendProfileOtp}
                disabled={otpDialog.sending}
                className="flex-1 px-4 py-2.5 rounded-xl border border-indigo-100 text-indigo-700 text-xs font-extrabold hover:bg-indigo-50 disabled:opacity-60"
              >
                {otpDialog.sending ? 'Sending...' : 'Resend OTP'}
              </button>
              <button
                onClick={handleVerifyProfileOtpAndSave}
                disabled={otpDialog.verifying || otpDialog.sending}
                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-extrabold hover:bg-black disabled:opacity-60"
              >
                {otpDialog.verifying ? 'Verifying...' : 'Verify & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-100 p-6 shadow-xl animate-scale-up">
            <div className="flex items-center gap-3 mb-4 text-rose-600">
              <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Remove Document</h3>
                <p className="text-xs text-slate-500 mt-0.5">Confirm document deletion</p>
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              Are you sure you want to remove <strong className="text-slate-800">"{confirmDelete.label}"</strong>? You will need to upload it again to complete your admission checklist.
            </p>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 transition-all cursor-pointer text-center animate-fade-in"
              >
                No, Cancel
              </button>
              <button
                onClick={async () => {
                  const { docKey } = confirmDelete;
                  setConfirmDelete(null);
                  await handleRemoveDoc(docKey);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-rose-600 text-white hover:bg-rose-700 transition-all cursor-pointer text-center shadow-sm"
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Admission Modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-100 p-6 shadow-xl animate-scale-up">
            <div className="flex items-center gap-3 mb-4 text-rose-600">
              <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Cancel Admission</h3>
                <p className="text-xs text-slate-500 mt-0.5">Application ID: {confirmCancel.applicationId}</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-red-800 mb-1">Warning: No Refund Policy</p>
              <p className="text-xs text-red-600 leading-relaxed">
                By proceeding with this cancellation, you acknowledge that the pre-registration fee is <strong className="font-bold">strictly non-refundable</strong>. This action will permanently release your secured seat and cannot be undone.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setConfirmCancel(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 transition-all cursor-pointer text-center animate-fade-in"
              >
                Keep Seat
              </button>
              <button
                onClick={handleCancelAdmission}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-rose-600 text-white hover:bg-rose-700 transition-all cursor-pointer text-center shadow-sm"
              >
                Yes, Cancel Seat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
